import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Users, GraduationCap, TrendingUp, Award } from 'lucide-react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js'
import { courseApi, type CourseStats } from '../api/lessonBuilderApi'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// Trang thống kê 1 khoá học (giáo viên): tổng quan + phễu từng bài + bảng học viên.
const CourseStatsPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<CourseStats | null | undefined>(undefined)

  useEffect(() => {
    if (id) courseApi.stats(id).then(setData).catch(() => setData(null))
  }, [id])

  if (data === undefined) return <div className="p-10 text-center text-gray-400">Đang tải thống kê...</div>
  if (!data) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-500 mb-4">Không xem được thống kê khoá học (chưa đăng nhập hoặc không có quyền).</p>
        <button onClick={() => navigate('/teacher/khoa-hoc')} className="text-blue-600 font-semibold">← Về danh sách khoá học</button>
      </div>
    )
  }

  const ov = data.overview
  const labels = data.perLesson.map((l, i) => `B${i + 1}`)
  const funnel = {
    labels,
    datasets: [
      { label: 'Bắt đầu', data: data.perLesson.map((l) => l.started), backgroundColor: '#93c5fd' },
      { label: 'Hoàn thành', data: data.perLesson.map((l) => l.completed), backgroundColor: '#10b981' },
    ],
  }
  const scoreChart = {
    labels,
    datasets: [
      { label: 'Điểm TB câu hỏi (%)', data: data.perLesson.map((l) => l.avgScore ?? 0), backgroundColor: '#f59e0b' },
    ],
  }
  const barOpts = { responsive: true, plugins: { legend: { position: 'top' as const } }, scales: { y: { beginAtZero: true } } }

  const Stat = ({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: React.ReactNode; color: string }) => (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
      <span className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: color }}>{icon}</span>
      <div>
        <div className="text-2xl font-black text-gray-900 leading-none">{value}</div>
        <div className="text-xs text-gray-500 mt-1">{label}</div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/teacher/khoa-hoc')} className="text-gray-400 hover:text-gray-700"><ArrowLeft size={20} /></button>
          <h1 className="text-lg font-black text-gray-900 truncate">Thống kê · {data.course.title}</h1>
          <button onClick={() => navigate(`/khoa-hoc/${data.course.slug}`)} className="ml-auto text-sm font-semibold text-blue-600 hover:underline whitespace-nowrap">Xem khoá ↗</button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {/* Tổng quan */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={<Users size={20} />} label="Học viên ghi danh" value={ov.enrolled} color="#2563eb" />
          <Stat icon={<Award size={20} />} label="Hoàn thành khoá" value={ov.completedCourse} color="#10b981" />
          <Stat icon={<TrendingUp size={20} />} label="Tiến độ trung bình" value={`${ov.avgPercent}%`} color="#f59e0b" />
          <Stat icon={<GraduationCap size={20} />} label="Số bài giảng" value={ov.lessons} color="#7c3aed" />
        </div>

        {ov.enrolled === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400">
            Chưa có học viên ghi danh khoá này. Khi học viên ghi danh & học, số liệu sẽ hiện ở đây.
          </div>
        ) : (
          <>
            {/* Phễu theo bài */}
            <section className="bg-white rounded-2xl border border-gray-100 p-5">
              <h2 className="text-base font-black text-gray-900 mb-1">Phễu hoàn thành theo bài</h2>
              <p className="text-xs text-gray-400 mb-4">Bao nhiêu học viên bắt đầu vs hoàn thành từng bài (B1, B2... theo thứ tự khoá).</p>
              <Bar data={funnel} options={barOpts} />
            </section>

            {/* Điểm TB câu hỏi theo bài */}
            <section className="bg-white rounded-2xl border border-gray-100 p-5">
              <h2 className="text-base font-black text-gray-900 mb-1">Điểm trung bình câu hỏi chốt theo bài</h2>
              <p className="text-xs text-gray-400 mb-4">% trả lời đúng các câu hỏi tương tác trong mỗi bài.</p>
              <Bar data={scoreChart} options={barOpts} />
            </section>

            {/* Bảng đối chiếu tên bài */}
            <section className="bg-white rounded-2xl border border-gray-100 p-5">
              <h2 className="text-base font-black text-gray-900 mb-3">Chi tiết theo bài</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Bài giảng</th>
                    <th className="py-2 px-2 text-center">Bắt đầu</th><th className="py-2 px-2 text-center">Hoàn thành</th>
                    <th className="py-2 px-2 text-center">Điểm TB</th><th className="py-2 px-2 text-center">% nội dung</th><th className="py-2 px-2 text-center">Hỏi AI</th>
                  </tr></thead>
                  <tbody>
                    {data.perLesson.map((l, i) => (
                      <tr key={l.id} className="border-b border-gray-50">
                        <td className="py-2 pr-2 text-gray-400">B{i + 1}</td>
                        <td className="py-2 pr-2 font-semibold text-gray-800">{l.title}</td>
                        <td className="py-2 px-2 text-center">{l.started}</td>
                        <td className="py-2 px-2 text-center text-emerald-600 font-bold">{l.completed}</td>
                        <td className="py-2 px-2 text-center">{l.avgScore == null ? '—' : `${l.avgScore}%`}</td>
                        <td className="py-2 px-2 text-center">{l.avgContent == null ? '—' : `${l.avgContent}%`}</td>
                        <td className="py-2 px-2 text-center">{l.aiAsked || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Theo học viên */}
            <section className="bg-white rounded-2xl border border-gray-100 p-5">
              <h2 className="text-base font-black text-gray-900 mb-3">Theo học viên ({data.perStudent.length})</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="py-2 pr-2">Học viên</th><th className="py-2 px-2 text-center">Hoàn thành</th>
                    <th className="py-2 px-2">Tiến độ</th><th className="py-2 px-2 text-center">Điểm TB</th>
                  </tr></thead>
                  <tbody>
                    {data.perStudent.map((s, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-2 pr-2">
                          <div className="font-semibold text-gray-800">{s.studentName || s.studentEmail || 'Học viên'}</div>
                          {s.studentName && s.studentEmail && <div className="text-[11px] text-gray-400">{s.studentEmail}</div>}
                        </td>
                        <td className="py-2 px-2 text-center text-gray-600">{s.completed}/{s.total}</td>
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden min-w-[80px]">
                              <div className="h-full bg-emerald-500" style={{ width: `${s.percent}%` }} />
                            </div>
                            <span className="text-xs font-bold text-gray-600 w-9 text-right">{s.percent}%</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-center">{s.avgScore == null ? '—' : `${s.avgScore}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}

export default CourseStatsPage
